import { type EntryIdInput, formatEntryId } from "./entryId";
import { createAction, type MutationAction } from "./mutationAction";
import {
	type ActionDeps,
	ApiError,
	actingHeaders,
	apiCall,
	plural,
	refreshEntries,
} from "./types";

/**
 * Bulk duplicate via the v2 atomic endpoint.
 *
 *   POST /v2/entries/duplicate { sourceIds: ["ent_<n>", …] }
 *
 * Sources can be in any state — `tracked`, `reviewed`, `exported`, or
 * `deleted`. The server creates a fresh `tracked` row for each, stamping
 * `duplicated_from_id` and `origin = "duplicate"`, leaving the source row
 * untouched (the export ledger stays intact, the archived row stays
 * archived). That makes Duplicate the only bulk action available on
 * exported / deleted selections.
 *
 * No optimistic insert — the new rows carry server-minted ids that the
 * cache doesn't have ahead of the response. The post-action refresh on
 * `entries` pulls them in.
 */
export function duplicateAction(
	ids: EntryIdInput[],
	deps: ActionDeps,
	timekeeperId?: string | null,
): MutationAction {
	const n = ids.length;
	const wireIds = ids.map(formatEntryId);

	return createAction({
		execute: () =>
			apiCall(deps, {
				endpoint: "/v2/entries/duplicate",
				method: "POST",
				body: { sourceIds: wireIds },
				headers: actingHeaders(timekeeperId),
			}),
		rollback: null,
		toast: {
			loading: `Duplicating ${n} ${plural(n)}…`,
			success: `Duplicated ${n} ${plural(n)}`,
			error: (err) => {
				if (err instanceof ApiError && err.code === "ENTRY_NOT_FOUND")
					return "One or more entries no longer exist";
				return "Failed to duplicate entries";
			},
		},
		onSuccess: () => refreshEntries(deps),
		onError: () => refreshEntries(deps),
	});
}
